import React, { useEffect, useState } from 'react';
import { compose } from 'redux';

import { TextFieldWithLabel } from '../../../shared/fields/text-fields/text-field-with-label';
import { TextFieldMenuCountry } from '../../../shared/fields/text-fields/text-field-menu-country/text-field-menu-country';
import { WrapperTwoTextField } from '../../../shared/fields/common.styles';
import { withBillingDetails } from './billing-details.hocs';
import { BILLING_DETAILS_TEXTS, BILLING_DETAILS_TEST_ID } from './billing-details.constants';
import * as S from './billing-details.styles';
import { buildPropsByCountry, formatModel, isInternational } from './billing-details.utils';
import { isDataLoading } from '../../../shared/axios';
import * as Types from './billing-details.types';
import { BillingDetailsIbge } from './billing-details-ibge';
import { withIbge } from '../ibge/ibge.hocs';
import {
    DefaultButton, ButtonColors, ButtonSize, ButtonVariant,
} from '../../../shared/buttons';

 interface BillingDetailsImplProps {
    billingDetails: Types.BillingDetails;
    onClickBack: (bool: boolean) => void;
    onSubmitBillingDetails: (details : Types.BillingDetails) => void;
    billingDetailsLoadState: string;
    billingDetailsErros: Types.BillingDetails;
    onGetIbgeStates: () => void;
    onGetIbgeCities: (stateCode: string) => void;
}

const BillingDetailsImpl = ({
    billingDetails,
    onClickBack,
    onSubmitBillingDetails,
    billingDetailsLoadState,
    billingDetailsErros: errors,
    onGetIbgeStates,
    onGetIbgeCities,
}: BillingDetailsImplProps) => {
    const [model, setModel] = useState(billingDetails);

    useEffect(() => {
        setModel(billingDetails);
    }, [billingDetails]);

    useEffect(() => {
        if (!isInternational(model.countryCode)) {
            onGetIbgeStates();
        }
    }, [model.countryCode, model.stateCode, onGetIbgeCities, onGetIbgeStates]);

    const { fieldOne, fieldTwo } = buildPropsByCountry(model, errors, setModel);

    const onSetModel = (newModel) => {
        setModel({ ...model, ...newModel });
    };

    const onSetCountry = (country) => {
        onSetModel({
            ...model, countryCode: country.value, city: '', state: '',
        });
    };

    const onSubmit = () => {
        onSubmitBillingDetails(formatModel(model));
    };

    const isEditingModel = !!model.id;

    return (
        <div>
            <div>
                <S.SubtitleWrapper>
                    <S.Subtitle>{BILLING_DETAILS_TEXTS.SUBTITLE}</S.Subtitle>
                </S.SubtitleWrapper>
            </div>

            <TextFieldMenuCountry
                label={BILLING_DETAILS_TEXTS.COUNTRY}
                onChange={onSetCountry}
                value={model.countryCode}
                error={errors.countryCode}
                isDisabled={isEditingModel}
            />

            <WrapperTwoTextField>
                {isInternational(model.countryCode) ? (
                    <>
                        <TextFieldWithLabel
                            label={BILLING_DETAILS_TEXTS.STATE}
                            value={model.state}
                            error={errors.state}
                            onChange={state => onSetModel({ state, city: '' })}
                        />
                        <TextFieldWithLabel
                            label={BILLING_DETAILS_TEXTS.CITY}
                            dataTestId={BILLING_DETAILS_TEST_ID.CITY}
                            value={model.city}
                            error={errors.city}
                            onChange={city => onSetModel({ city })}
                        />
                    </>
                ) : (
                    <BillingDetailsIbge
                        onChange={onSetModel}
                        errors={errors}
                        state={model.state}
                        city={model.cityCode}
                        cityCode={model.cityCode}
                    />
                )}
            </WrapperTwoTextField>

            <WrapperTwoTextField>
                <TextFieldWithLabel
                    label={BILLING_DETAILS_TEXTS.DISTRICT}
                    value={model.district}
                    error={errors.district}
                    onChange={district => onSetModel({ district })}
                    maxLength={50}
                />
                <TextFieldWithLabel {...fieldOne} />
            </WrapperTwoTextField>

            <WrapperTwoTextField>
                <TextFieldWithLabel
                    label={BILLING_DETAILS_TEXTS.STREET}
                    value={model.street}
                    error={errors.street}
                    onChange={street => onSetModel({ street })}
                    maxLength={128}
                />
                <TextFieldWithLabel
                    label={BILLING_DETAILS_TEXTS.NUMBER}
                    value={model.number}
                    error={errors.number}
                    onChange={number => onSetModel({ number })}
                    maxLength={50}
                />
            </WrapperTwoTextField>
            <TextFieldWithLabel
                label={BILLING_DETAILS_TEXTS.EMAIL}
                value={model.email}
                error={errors.email}
                onChange={email => onSetModel({ email })}
                maxLength={128}
            />
            <WrapperTwoTextField>
                <TextFieldWithLabel
                    label={BILLING_DETAILS_TEXTS.PHONE_NUMBER}
                    value={model.phoneNumber}
                    error={errors.phoneNumber}
                    onChange={phoneNumber => onSetModel({ phoneNumber })}
                    maxLength={20}
                />
                <TextFieldWithLabel {...fieldTwo} />
            </WrapperTwoTextField>

            <S.StyledWrapperTwoButtons>
                <DefaultButton
                    size={ButtonSize.SMALL}
                    label={BILLING_DETAILS_TEXTS.BACK}
                    onClick={() => onClickBack(false)}
                    variant={ButtonVariant.OUTLINED}
                />

                <DefaultButton
                    color={ButtonColors.ADDING}
                    label={BILLING_DETAILS_TEXTS.SUBMIT}
                    onClick={onSubmit}
                    isLoading={isDataLoading(billingDetailsLoadState)}
                    dataTestId={BILLING_DETAILS_TEST_ID.SUBMIT}
                    variant={ButtonVariant.CONTAINED}
                />
            </S.StyledWrapperTwoButtons>
        </div>
    );
};

const enhancers = compose(
    withBillingDetails,
    withIbge,
);

export const BillingDetails = enhancers(BillingDetailsImpl);
